import { FormEvent, useCallback, useEffect, useState } from 'react';
import { API_URLS } from '@/constants';
import { onAuthRequired } from '@/utils/auth';
import { authenticatedFetch } from '@/utils/api';

export interface Comment {
  id: number;
  prompt_id: number;
  user_id: string;
  first_name: string;
  last_name: string;
  votes_count: number;
  myVote: number;
  content: string;
  created_at: Date;
}

export const useComments = ({ promptId, isLoggedIn, currentUserId }: { promptId: number, isLoggedIn: boolean, currentUserId: string; }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Fetch comments for the specific prompt 
  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const response = await authenticatedFetch(API_URLS.COMMENTS(promptId));
      if (response.ok) {
        const baseComments = await response.json();

        const enrichedComments = await Promise.all(
          baseComments.map(async (comment: Comment) => {
            try {
              const voteResponse = await authenticatedFetch(`${API_URLS.VOTE_COMMENT(comment.id)}?user=${currentUserId}`);

              if (!voteResponse.ok) throw new Error(`Error votes: ${voteResponse.statusText}`);
              const voteData = await voteResponse.json();

              return {
                ...comment,
                votes_count: Number(voteData.vote_score ?? 0),
                myVote: Number(voteData.user_vote ?? 0),
              };
            } catch (err) {
              console.error(`Error fetching votes for comment ${comment.id}:`, err);
              return {
                ...comment,
                votes_count: 0,
                myVote: 0,
              };
            }
          })
        );

        setComments(enrichedComments);
      } else {
        console.error('CommentsSection: Error fetching comments:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('CommentsSection: Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [currentUserId, promptId]);

  useEffect(() => {
    fetchComments();
  }, [promptId, isLoggedIn, currentUserId, fetchComments]); // Depend on promptId, isLoggedIn, currentUserId to re-fetch if they change

  // Handle adding a new comment 
  // Security Improvement: Added 401 handling.
  const handleAddComment = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (!isLoggedIn) {
        onAuthRequired();
        return;
      }

      const response = await authenticatedFetch(API_URLS.COMMENTS(promptId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newCommentContent, userId: currentUserId }),
      });

      if (!response.ok) {
        console.error('Error en la solicitud:', await response.text());
        return;
      }

      const newComment = await response.json();

      setComments((prevComments) => [...prevComments, newComment]);
      setNewCommentContent('');
    } catch (err) {
      console.error('Error al enviar comentario:', err);
    }
  };

  // Handle voting on comments 
  // Security Improvement: Added 401 handling.
  const handleCommentVote = useCallback(async (commentId: number, voteType: 1 | -1) => {
    if (!isLoggedIn) {
      onAuthRequired();
      setStatusMessage('Please log in to vote on comments.');
      setTimeout(() => setStatusMessage(''), 3000);
      return;
    }

    setStatusMessage('Recording comment vote...');
    try {
      const response = await authenticatedFetch(API_URLS.VOTE_COMMENT(commentId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ voteType, userId: currentUserId })
      });

      if (!response.ok) {
        if (response.status === 401) {
          setStatusMessage('Unauthorized. Please log in again.');
          onAuthRequired(); // Security Improvement: Handle 401 by logging out
          console.error('CommentsSection: Comment voting error: Unauthorized.');
        } else if (response.status === 409) {
          setStatusMessage('You have already voted on this comment.');
          console.warn('CommentsSection: Comment voting error: Already voted.');
        } else {
          setStatusMessage(`Failed to record comment vote: ${response.statusText || 'Unknown error'}`);
          console.error('CommentsSection: Comment voting error:', response.status, response.statusText);
        }
      } else {
        setComments((prevComments) =>
          prevComments.map((comment) => {
            if (comment.id === commentId) {
              const oldVote = comment.myVote || 0;
              const newVote = voteType;

              if (oldVote === newVote) return comment;

              const voteAdjustment = newVote - oldVote;

              return {
                ...comment,
                myVote: newVote,
                votes_count: (comment.votes_count || 0) + voteAdjustment,
              };
            }
            return comment;
          })
        );
      }
    } catch (error) {
      console.error('CommentsSection: Error voting on comment:', error);
      setStatusMessage('Failed to record comment vote. Please try again.');
    } finally {
      setTimeout(() => setStatusMessage(''), 3000);
    }
  }, [currentUserId, isLoggedIn]);

  return {
    comments,
    commentsLoading,
    newCommentContent,
    statusMessage,
    handleAddComment,
    handleCommentVote,
    setNewCommentContent,
    setStatusMessage,
  };
};
